import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckoutButton } from "@/lib/checkout";
import { Zap, Sparkles, Check, Video, Lock } from "lucide-react";
import { useTr } from "@/i18n/translator";

const FREQS = [
  { id: "daily", label: "UNO AL DÍA", sub: "máximo mensual: 30" },
  { id: "weekly", label: "UNO POR SEMANA", sub: "máximo mensual: 4" },
  { id: "biweekly", label: "CADA 15 DÍAS", sub: "máximo mensual: 2" },
  { id: "custom", label: "PERSONALIZADO", sub: "tú defines" },
];

export default function Subscription() {
  const tr = useTr();
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [videoSettings, setVideoSettings] = useState(null);
  const [customDays, setCustomDays] = useState(7);
  const [toggling, setToggling] = useState(false);
  const [savingFreq, setSavingFreq] = useState(false);
  const [videoMode, setVideoMode] = useState(false);
  const { user, refresh } = useAuth();

  const load = async () => {
    const [s, p, vs] = await Promise.all([
      api.get("/billing/subscription"),
      api.get("/pricing"),
      api.get("/videos-settings"),
    ]);
    setSub(s.data);
    setPlans(p.data.plans);
    setVideoSettings(vs.data);
    if (vs.data.video_custom_days) setCustomDays(vs.data.video_custom_days);
    if (vs.data.has_video_addon) setVideoMode(true);
  };
  useEffect(() => { load(); }, []);

  const toggleAutopilot = async () => {
    setToggling(true);
    try {
      const { data } = await api.post("/auth/autopilot", { enabled: !user?.autopilot_enabled });
      await refresh();
      toast.success(data.autopilot_enabled ? "Autopilot activado" : "Autopilot desactivado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setToggling(false);
    }
  };

  const saveFrequency = async (frequency, days) => {
    setSavingFreq(true);
    try {
      const body = { frequency };
      if (frequency === "custom") body.custom_days = days || customDays;
      const { data } = await api.patch("/videos-settings", body);
      setVideoSettings(data);
      toast.success("Frecuencia actualizada");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSavingFreq(false);
    }
  };

  if (!sub || !videoSettings) return <div className="font-mono-mark text-xs text-[#5C5C5C]">CARGANDO…</div>;

  const curFreq = videoSettings.video_frequency;
  const hasVideo = videoSettings.has_video_addon;

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("TARIFA · PLAN ACTIVO")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("Suscripción y uso")}</h1>
      </div>

      {/* Plan hero */}
      <div className="grid md:grid-cols-3 gap-0 border border-[#0A0A0A]">
        <div className="bg-[#0A0A0A] text-white p-8 md:col-span-2 flex flex-col justify-between">
          <div>
            <div className="font-mono-mark text-[10px] text-[#FFD600]">{tr("PLAN ACTIVO")}</div>
            <div className="font-display text-6xl mt-3 uppercase">
              {sub.plan}{hasVideo && <span className="text-[#E53935]"> +V</span>}
            </div>
            {sub.plan !== sub.base_plan && (
              <div className="font-mono-mark text-[10px] text-[#FFD600] mt-3">
                BONUS REFERIDO · TU PLAN BASE ES {sub.base_plan.toUpperCase()}
              </div>
            )}
            {hasVideo && (
              <div className="font-mono-mark text-[10px] text-[#FFD600] mt-3 flex items-center gap-2">
                <Video className="w-3 h-3" /> {tr("ADD-ON DE VIDEO IA ACTIVO")}
              </div>
            )}
          </div>
          <div className="mt-8 flex items-center gap-3">
            {sub.limits.autopilot ? (
              <button
                onClick={toggleAutopilot}
                disabled={toggling}
                className={`font-mono-mark text-[10px] px-4 py-3 border flex items-center gap-2 ${
                  user?.autopilot_enabled
                    ? "bg-[#E53935] text-white border-[#E53935]"
                    : "bg-transparent text-white border-white hover:bg-white hover:text-[#0A0A0A]"
                }`}
                data-testid="sub-autopilot-toggle"
              >
                <Zap className="w-3 h-3" />
                {tr("AUTOPILOT")} {user?.autopilot_enabled ? tr("ACTIVO") : tr("INACTIVO")}
              </button>
            ) : (
              <span className="font-mono-mark text-[10px] text-white/50">{tr("AUTOPILOT · PLAN PRO REQUERIDO")}</span>
            )}
          </div>
        </div>
        <div className="bg-white p-8 space-y-4">
          <Usage label={tr("POSTS/MES")} used={sub.usage.posts} max={sub.limits.posts} />
          <Usage label={tr("IMÁGENES/MES")} used={sub.usage.images} max={sub.limits.images} />
          <Usage label="REDES CONECTADAS" used={sub.usage.networks} max={sub.limits.networks} />
          <Usage label="COMPETIDORES" used={sub.usage.competitors} max={sub.limits.competitors} />
          {hasVideo && <Usage label="VIDEO HOY" used={videoSettings.today_used} max={1} />}
        </div>
      </div>

      {/* Video Add-On Section */}
      <div className="bg-white border border-[#0A0A0A] p-6" data-testid="video-addon-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#E53935] text-white flex items-center justify-center">
            <Video className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("ADD-ON · VIDEO IA")}</div>
            <h2 className="font-display text-2xl">{tr("Videos IA verticales")}</h2>
          </div>
          <span className={`font-mono-mark text-[10px] px-3 py-2 ${hasVideo ? "bg-[#E53935] text-white" : "bg-[#E5E5E5] text-[#0A0A0A]"}`} data-testid="video-addon-status">
            {hasVideo ? "ACTIVO" : "INACTIVO"}
          </span>
        </div>

        {!hasVideo ? (
          <div>
            <p className="font-mono-data text-sm text-[#5C5C5C]">
              Activa el add-on de Video para generar contenido Reels/TikTok/Shorts con IA.
              Cuesta <b className="text-[#0A0A0A]">{tr("2× el precio de tu plan")}</b> {tr("y permite hasta")} <b className="text-[#0A0A0A]">{tr("1 video por día")}</b>.
              Puedes elegir qué tan seguido: diario, semanal, cada 15 días o personalizado.
            </p>
            {sub.base_plan === "free" ? (
              <p className="mt-3 font-mono-mark text-[10px] text-[#E53935]">
                {tr("REQUIERE PLAN PAGO · ELIGE UN PLAN DE ABAJO CON LA OPCIÓN +VIDEO")}
              </p>
            ) : (
              <CheckoutButton
                planId={`${sub.base_plan}_video`}
                variant="red"
                className="mt-4"
                data-testid="activate-video-addon"
              >
                <Video className="w-4 h-4" /> {tr("ACTIVAR +VIDEO")} · ${plans.find((p) => p.id === sub.base_plan)?.video_price_usd}/{tr("MES")}
              </CheckoutButton>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            <p className="font-mono-data text-sm text-[#5C5C5C]">
              {tr("Tienes el add-on de Video activo. Elige tu frecuencia máxima (nunca más de 1 video/día):")}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {FREQS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => saveFrequency(f.id, customDays)}
                  disabled={savingFreq}
                  className={`p-3 text-left font-mono-mark text-[10px] border ${
                    curFreq === f.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#0A0A0A] hover:bg-[#F4F4F0]"
                  }`}
                  data-testid={`freq-${f.id}`}
                >
                  <div>{f.label}</div>
                  <div className={`text-[9px] mt-1 ${curFreq === f.id ? "text-[#FFD600]" : "text-[#5C5C5C]"}`}>{f.sub}</div>
                </button>
              ))}
            </div>

            {curFreq === "custom" && (
              <div className="flex items-center gap-3 border border-[#0A0A0A] p-4" data-testid="freq-custom-row">
                <label className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("CADA CUÁNTOS DÍAS:")}</label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={customDays}
                  onChange={(e) => setCustomDays(Math.max(1, Number(e.target.value)))}
                  className="w-20 bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm"
                  data-testid="freq-custom-days"
                />
                <button onClick={() => saveFrequency("custom", customDays)} disabled={savingFreq} className="font-mono-mark text-[10px] bg-[#E53935] text-white px-4 py-2 border border-[#E53935]" data-testid="freq-custom-save">
                  {tr("GUARDAR")}
                </button>
              </div>
            )}

            <div className="flex items-baseline justify-between border-t border-[#0A0A0A] pt-4">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("MÁXIMO MENSUAL CON ESTA FRECUENCIA")}</span>
              <span className="font-display text-3xl font-mono-data" data-testid="video-monthly-cap">{videoSettings.monthly_cap}</span>
            </div>
          </div>
        )}
      </div>

      {/* Plan selector */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("CAMBIAR PLAN")}</div>
            <h2 className="font-display text-3xl mt-2">{tr("Mejora cuando lo necesites")}</h2>
          </div>
          <div className="inline-flex border border-[#0A0A0A]" data-testid="sub-video-toggle">
            <button onClick={() => setVideoMode(false)} className={`font-mono-mark text-[10px] px-4 py-2 ${!videoMode ? "bg-[#0A0A0A] text-white" : "bg-white"}`} data-testid="sub-toggle-standard">
              {tr("ESTÁNDAR")}
            </button>
            <button onClick={() => setVideoMode(true)} className={`font-mono-mark text-[10px] px-4 py-2 flex items-center gap-2 ${videoMode ? "bg-[#E53935] text-white" : "bg-white"}`} data-testid="sub-toggle-video">
              <Video className="w-3 h-3" /> {tr("+VIDEO (2×)")}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-0 border border-[#0A0A0A] mt-6">
          {plans.map((p, i) => {
            const isCurrent = sub.base_plan === p.id && (videoMode ? hasVideo : !hasVideo);
            const h = p.highlight;
            const priceLabel = videoMode && p.id !== "free" ? p.video_price_usd : p.price_usd;
            const planKeyForCheckout = p.id === "free" ? "free" : (videoMode ? `${p.id}_video` : p.id);
            return (
              <div key={p.id} className={`p-6 flex flex-col ${i < plans.length - 1 ? "border-b md:border-b-0 md:border-r border-[#0A0A0A]" : ""} ${h ? "bg-[#0A0A0A] text-white" : "bg-white"}`} data-testid={`sub-plan-${p.id}`}>
                <div className={`font-mono-mark text-[10px] ${h ? "text-[#FFD600]" : "text-[#5C5C5C]"}`}>
                  {tr(p.name).toUpperCase()}{videoMode && p.id !== "free" && " +V"} {isCurrent && "· " + tr("ACTUAL")}
                </div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-4xl">${priceLabel}</span>
                  <span className="font-mono-mark text-[9px]">/{tr(p.period)}</span>
                </div>
                <ul className="mt-4 space-y-1.5 text-xs font-mono-data flex-1">
                  {p.features.slice(0, 5).map((f) => (
                    <li key={f} className="flex gap-2">
                      <Check className="w-3 h-3 text-[#E53935] shrink-0 mt-0.5" />
                      <span>{tr(f)}</span>
                    </li>
                  ))}
                  {videoMode && p.id !== "free" && (
                    <li className="flex gap-2 font-bold">
                      <Video className="w-3 h-3 text-[#E53935] shrink-0 mt-0.5" />
                      <span>{tr("Hasta 30 videos IA/mes")}</span>
                    </li>
                  )}
                </ul>
                <div className="mt-6">
                  {isCurrent ? (
                    <div className="text-center font-mono-mark text-[10px] px-3 py-2 border border-[#E53935] text-[#E53935]">ACTUAL</div>
                  ) : p.id === "free" ? (
                    <div className="text-center font-mono-mark text-[10px] px-3 py-2 text-[#5C5C5C]">—</div>
                  ) : (
                    <CheckoutButton planId={planKeyForCheckout} variant={h ? "red" : "outline"} className="w-full">
                      {tr("ELEGIR")}
                    </CheckoutButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-4 font-mono-mark text-[10px] text-[#5C5C5C]">
          STRIPE MODO TEST · TARJETA 4242 4242 4242 4242 · CVV 123 · FECHA FUTURA
        </p>
      </div>
    </div>
  );
}

function Usage({ label, used, max }) {
  const unlimited = max == null;
  const percent = unlimited ? 5 : Math.min(100, (used / max) * 100);
  return (
    <div>
      <div className="flex justify-between font-mono-mark text-[10px] text-[#5C5C5C]">
        <span>{label}</span>
        <span className="font-mono-data text-[11px] text-[#0A0A0A]">{used} / {unlimited ? "∞" : max}</span>
      </div>
      <div className="h-1.5 bg-[#E5E5E5] mt-1 relative">
        <div className={`absolute top-0 left-0 bottom-0 ${percent > 80 ? "bg-[#E53935]" : "bg-[#0A0A0A]"}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}
